import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, formatApiErrorDetail } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { MapPin } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useBranding } from "../contexts/BrandingContext";

export default function SignupPage() {
  const { t, i18n } = useTranslation();
  const { branding } = useBranding();
  const brandName = (i18n.language === "el" && branding?.brand_name_el) || branding?.brand_name || "TableCy";
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { register, user } = useAuth();
  const navigate = useNavigate();

  // Redirect if already logged in
  if (user) {
    navigate("/account", { replace: true });
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }

    setLoading(true);

    try {
      await register(email, password, name, phone || null);
      navigate("/account");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignup = () => {
    // Use a RELATIVE URL so the OAuth flow stays on the same user-facing host
    // (e.g. tablecy.com → callback + Google consent screen both show tablecy.com).
    // The backend builds the callback URI dynamically from the Host header.
    const redirectPath = "/account";
    window.location.href = `/api/auth/google/login?redirect=${encodeURIComponent(redirectPath)}`;
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left Side - Image */}
      <div className="hidden lg:flex lg:w-1/2 relative">
        <img
          src="https://images.unsplash.com/photo-1546062150-c919e93704ac?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwxfHxjeXBydXMlMjByZXN0YXVyYW50JTIwbWVkaXRlcnJhbmVhbnxlbnwwfHx8fDE3NzU4NTk3MzR8MA&ixlib=rb-4.1.0&q=85"
          alt="Mediterranean restaurant"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <h2 className="text-4xl font-medium mb-4 text-shadow-lg">{t("signup:title")}</h2>
          <p className="text-lg text-white/90 text-shadow">{t("signup:subtitle")}</p>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md space-y-8">
          {/* Logo (reads from branding settings so admin changes propagate) */}
          <Link to="/" className="flex items-center gap-2 text-primary" data-testid="signup-brand-logo">
            {branding?.use_image && branding?.logo_url ? (
              <img src={branding.logo_url} alt={brandName} className="h-8 w-8 object-contain" />
            ) : branding?.logo_svg ? (
              <span className="h-8 w-8 inline-flex items-center justify-center" dangerouslySetInnerHTML={{ __html: branding.logo_svg }} />
            ) : (
              <MapPin className="h-8 w-8" />
            )}
            <span className="text-2xl font-semibold">{brandName}</span>
          </Link>

          <div>
            <h1 className="text-3xl font-medium text-foreground">{t("signup:title")}</h1>
            <p className="mt-2 text-muted-foreground">
              {t("signup:has_account")}{" "}
              <Link to="/login" className="text-primary hover:underline">
                {t("signup:login")}
              </Link>
            </p>
          </div>

          {error && (
            <div className="bg-destructive/10 text-destructive px-4 py-3 rounded-lg text-sm" data-testid="signup-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="name">{t("signup:name")}</Label>
              <Input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="John Doe"
                required
                data-testid="signup-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">{t("signup:email")}</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                data-testid="signup-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">{t("signup:phone")}</Label>
              <Input
                id="phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+357 99 123456"
                data-testid="signup-phone-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">{t("signup:password")}</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                data-testid="signup-password-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">{t("reset:confirm")}</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                required
                data-testid="signup-confirm-password-input"
              />
            </div>

            <Button type="submit" className="w-full" disabled={loading} data-testid="signup-submit-button">
              {loading ? "Creating account..." : "Create account"}
            </Button>
          </form>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="bg-background px-4 text-muted-foreground">Or continue with</span>
            </div>
          </div>

          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={handleGoogleSignup}
            data-testid="signup-google-button"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="currentColor"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="currentColor"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="currentColor"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            {t("signup:google")}
          </Button>

          <p className="text-xs text-muted-foreground text-center">
            By creating an account, you agree to our{" "}
            <Link to="/page/terms-of-service" className="underline">
              Terms of Service
            </Link>{" "}
            and{" "}
            <Link to="/page/privacy-policy" className="underline">
              Privacy Policy
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
